import { CREDIT_META } from "./data";
import styles from "./pricing.module.css";
import FAQSection from "./FaqSection";
import { fmtCurrency } from "../../../../../utils/functions";

const TRACKS = [
  {
    icon: "🪙",
    label: "Credit Packs",
    tag: "NO SUBSCRIPTION",
    variantClass: "trackPacks",
    desc: "TBuy a credit pack when you're ready to hire. No recurring charge, no commitment. Credits never expire and stack across purchases.",
    best: "Best for: occasional hiring · testing AwaSource · SMEs with unpredictable needs",
  },
  {
    icon: "📆",
    label: "Annual Plans",
    tag: "SAVE UP TO 60%",
    variantClass: "trackAnnual",
    desc: "Pay once, hire all year. Get a monthly credit allocation that rolls over for 12 months, plus team features, analytics, and priority support.",
    best: "Best for: consistent year-round hiring · growing teams · 6+ hires/yr",
  },
];

const BENEFITS = [
  { icon: "💳", label: "One payment", desc: "All credits issued upfront" },
  {
    icon: "🔄",
    label: "Credits never lost",
    desc: "Frozen if plan lapses, not lost",
  },
  { icon: "⚡", label: "Lower per-credit", desc: null }, // filled from annLow below
  {
    icon: "🏆",
    label: "Premium features",
    desc: "Team, analytics, API, support",
  },
];

const POLICIES = [
  {
    icon: "🛡️",
    title: "30-Day Rematch Guarantee",
    desc: "If a hire leaves within 30 days, we rematch at 50% of the original credit cost — on both packs and annual plans.",
  },
  {
    icon: "🔄",
    title: "Credits Never Lost",
    desc: "Unused credits roll over indefinitely. If your plan lapses they're frozen — not lost — and unlock the moment you reactivate.",
  },
  {
    icon: "📅",
    title: "1-Year Validity",
    desc: "Every plan purchase grants 12 months of access from the purchase date. A new purchase resets the clock — it doesn't stack.",
  },
  { icon: "⚡", title: "Top-ups on Any Plan", desc: null }, // filled from topUpRange
];

const PLAN_ACCENT = {
  Starter: "planStarter",
  Growth: "planGrowth",
  Scale: "planScale",
  Enterprise: "planEnterprise",
};

// ── Feature list builders ─── //

const buildPackFeatures = (plan) => {
  if (!plan) return [];
  return [
    { ok: plan?.aiCandidateMatching, text: "AI-powered candidate matching" },
    { ok: plan?.top5CandidateShortlist, text: "Top candidates shortlist" },
    { ok: plan?.creditsNeverExpire, text: "Credits never expire" },
    { ok: plan?.packsStack, text: "Packs stack — buy more any time" },
    { ok: plan?.emailSupport, text: "Email support" },
    { ok: plan?.teamCollaboration, text: "Team collaboration" },
    { ok: plan?.advancedAnalytics, text: "Advanced analytics" },
    { ok: plan?.apiAccess, text: "API access" },
  ];
};

const buildAnnualFeatures = (plan) => {
  const f = [];
  if (plan?.creditUnits)
    f.push(`${plan?.creditUnits} credits upfront, valid 1 year`);
  if (plan?.candidateShortlistLimit != null)
    f.push(
      `${plan?.candidateShortlistLimit === -1 ? "Unlimited" : plan?.candidateShortlistLimit} candidate shortlists`,
    );
  if (plan?.activeJobPostsLimit != null && plan?.activeJobPostsLimit !== 0)
    f.push(
      `${plan?.activeJobPostsLimit === -1 ? "Unlimited" : plan?.activeJobPostsLimit} active job posts`,
    );
  if (plan?.emailSupport) f.push("Email support");
  if (plan?.prioritySupport) f.push("Priority support");
  if (plan?.teamCollaborationSeats > 0)
    f.push(
      `${plan?.teamCollaborationSeats === -1 ? "Unlimited" : plan?.teamCollaborationSeats} team seats`,
    );
  if (plan?.advancedAnalyticsDashboard) f.push("Advanced analytics dashboard");
  if (plan?.aiWorkforceInsights) f.push("AI workforce insights");
  if (plan?.dedicatedSuccessManager) f.push("Dedicated success manager");
  if (plan?.apiAccess) f.push("API access");
  if (plan?.customCreditVolume) f.push("Custom credit volume");
  if (plan?.hrisAtsIntegrations) f.push("HRIS / ATS integrations");
  if (plan?.whiteGloveOnboarding) f.push("White-glove onboarding");
  if (plan?.engineeringSupport) f.push("Engineering support");
  return f;
};

export default function PlansTab({
  currency,
  annualPlans = [],
  topUpPlans = [],
  activeSubscription,
  onBuyPack,
  onStartPlan,
  onContactSales,
}) {
  const isNGN = currency === "NGN";

  // Sort plans by price ascending so UI order is always Starter → Growth → Scale
  const sortedTopUp = [...topUpPlans]?.sort(
    (a, b) => a?.priceNGN - b?.priceNGN,
  );
  const sortedAnnual = [
    ...annualPlans
      ?.filter((p) => !p?.isCustomPricing)
      ?.sort((a, b) => a?.priceNGN - b?.priceNGN),
    ...annualPlans?.filter((p) => p?.isCustomPricing), // Enterprise always last
  ];

  // ── Per-credit ranges from real data ──────────────────────────────────────
  const packPerCredit = sortedTopUp?.map((p) =>
    isNGN ? p?.priceNGN / p?.creditUnit : p?.priceUSD / p?.creditUnit,
  );
  const annualPerCredit = sortedAnnual
    ?.filter((p) => !p?.isCustomPricing && p?.creditUnits)
    ?.map((p) =>
      isNGN ? p?.priceNGN / p?.creditUnits : p?.priceUSD / p?.creditUnits,
    );

  const packLow = packPerCredit?.length
    ? fmtCurrency(Math.min(...packPerCredit), currency)
    : isNGN
      ? "₦7,000"
      : "$10";
  const packHigh = packPerCredit?.length
    ? fmtCurrency(Math.max(...packPerCredit), currency)
    : isNGN
      ? "₦7,000"
      : "$10";
  const annLow = annualPerCredit?.length
    ? fmtCurrency(Math.min(...annualPerCredit), currency)
    : isNGN
      ? "₦5,000"
      : "$3.57";
  const annHigh = annualPerCredit?.length
    ? fmtCurrency(Math.max(...annualPerCredit), currency)
    : isNGN
      ? "₦7,500"
      : "$6";
  const topUpRange = `${packLow}–${packHigh}`;

  // Active plan — only highlight when subscription is genuinely active
  const activePlanName = activeSubscription?.isActive
    ? activeSubscription.planName
    : null;

  return (
    <div>
      {/* ── Credit scale ── */}
      <div className={styles.creditScale}>
        <div className={styles.creditScaleLabel}>What 1 credit buys you</div>
        <div className={styles.creditScaleGrid}>
          {Object.entries(CREDIT_META).map(([level, meta]) => (
            <div key={level} className={styles.creditScaleCell}>
              <div
                className={`${styles.creditScaleCircle} ${styles["creditLevel" + level]}`}
              >
                {level}
              </div>
              <div
                className={`${styles.creditScaleCellLabel} ${styles["creditText" + level]}`}
              >
                {meta.label}
              </div>
              <div className={styles.creditScaleCellExample}>
                {meta.example}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Track selector cards ── */}
      <div className={styles.trackGrid}>
        {TRACKS.map((t) => (
          <div
            key={t.label}
            className={`${styles.trackCard} ${styles[t.variantClass]}`}
          >
            <div className={styles.trackHeader}>
              <span className={styles.trackIcon}>{t.icon}</span>
              <span className={styles.trackLabel}>{t.label}</span>
              <span className={styles.trackTag}>{t.tag}</span>
            </div>
            <p className={styles.trackDesc}>{t.desc}</p>
            <div className={styles.trackBest}>{t.best}</div>
          </div>
        ))}
      </div>

      {/* ══ TRACK 1 — CREDIT PACKS ══════════════════════════════════════════ */}
      <div>
        <div className={styles.trackSectionHeader}>
          <div
            className={`${styles.trackSectionBar} ${styles.trackSectionBarGreen}`}
          />
          <div>
            <div className={styles.trackSectionTitle}>Credit Packs</div>
            <div className={styles.trackSectionSub}>
              One-time purchase · {packLow}–{packHigh}/credit · credits never expire
            </div>
          </div>
        </div>

        <div className={styles.packOuterGrid}>
          <div className={styles.packCardsGrid}>
            {sortedTopUp?.map((plan, i) => {
              // Use priceNGN / priceUSD directly — no conversion math needed
              const displayPrice = isNGN ? plan?.priceNGN : plan?.priceUSD;
              const displayPerC = isNGN
                ? plan?.priceNGN / plan?.creditUnit
                : plan?.priceUSD / plan?.creditUnit;
              const isActive = activePlanName === plan.name;
              const isFeatured = i === 1; // middle card (Growth Pack after sort)

              return (
                <div
                  key={plan?._id}
                  className={`${styles.packCard} ${isFeatured ? styles.packCardFeatured : ""}`}
                >
                  {isFeatured && (
                    <div className={styles.packBadge}>Most Popular</div>
                  )}
                  <div
                    className={
                      isFeatured
                        ? styles.packCardBodyOffset
                        : styles.packCardBody
                    }
                  >
                    <div className={styles.packName}>{plan.name}</div>
                    <div className={styles.packCreditsRow}>
                      <div className={styles.packCreditsNum}>
                        <span className={styles.packCreditsValue}>
                          {plan?.creditUnit}
                        </span>
                        <span className={styles.packCreditsLabel}>credits</span>
                      </div>
                      <div className={styles.packBest}>
                        good for {Math.ceil(plan?.creditUnit / 3)}–
                        {plan?.creditUnit} hires
                      </div>
                    </div>
                    <div className={styles.packPriceBlock}>
                      <div className={styles.packPriceValue}>
                        {fmtCurrency(displayPrice, currency)}
                      </div>
                      <div className={styles.packPriceSub}>
                        one-time · {fmtCurrency(displayPerC, currency)}/credit
                      </div>
                    </div>

                    {isActive ? (
                      <button
                        className={`${styles.packBuyBtn} ${styles.packBuyBtnCurrent}`}
                        disabled
                      >
                        ✓ Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => onBuyPack(plan)}
                        className={`${styles.packBuyBtn} ${isFeatured ? styles.packBuyBtnFeatured : ""}`}
                      >
                        Buy {plan?.name}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.packFeaturesPanel}>
            <div className={styles.packFeaturesLabel}>All packs include</div>
            <div className={styles.packFeaturesList}>
              {buildPackFeatures(sortedTopUp[0])?.map((f, i) => (
                <div key={i} className={styles.packFeatureRow}>
                  <span
                    className={`${styles.packFeatureIcon} ${f.ok ? styles.packFeatureIconOk : styles.packFeatureIconNo}`}
                  >
                    {f?.ok ? "✓" : "—"}
                  </span>
                  <span
                    className={`${styles.packFeatureText} ${f.ok ? styles.packFeatureTextOk : styles.packFeatureTextNo}`}
                  >
                    {f?.text}
                  </span>
                </div>
              ))}
            </div>
            <div className={styles.packFeaturesNote}>
              <div className={styles.packFeaturesNoteText}>
                Credits stack across purchases and are never lost. A top-up
                extends your account validity by 1 month only when less than a
                month remains — otherwise it simply adds credits.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── OR divider ── */}
      <div className={styles.orDivider}>
        <div className={styles.orDividerLine} />
        <div className={styles.orDividerCenterWrap}>
          <div className={styles.orDividerBadgeBg}>
            <div className={styles.orDividerBadge}>OR</div>
          </div>
        </div>
      </div>

      {/* ── Break-even band ── */}
      <div className={styles.breakEvenBand}>
        <div className={styles.breakEvenPack}>
          <div className={styles.breakEvenLabel}>🪙 Buying packs</div>
          <div className={styles.breakEvenValue}>
            {packLow}–{packHigh}
            <span className={styles.breakEvenUnit}>/credit</span>
          </div>
          <div className={styles.breakEvenDesc}>
            Best when you hire occasionally. No commitment, credits never
            expire.
          </div>
        </div>
        <div className={styles.breakEvenCenter}>
          <div className={styles.breakEvenCenterLabel}>Break-even point</div>
          <div className={styles.breakEvenCenterValue}>4–5 months</div>
          <div className={styles.breakEvenCenterSub}>
            of hiring before annual wins
          </div>
        </div>
        <div className={styles.breakEvenAnnual}>
          <div className={styles.breakEvenLabel}>Going annual 📆</div>
          <div className={styles.breakEvenValue}>
            {annLow}–{annHigh}
            <span className={styles.breakEvenUnit}>/credit</span>
          </div>
          <div className={styles.breakEvenDesc}>
            Best for consistent hiring. Up to 60% cheaper, plus team features.
          </div>
        </div>
      </div>

      {/* ══ TRACK 2 — ANNUAL PLANS ══════════════════════════════════════════ */}
      <div>
        <div
          className={`${styles.trackSectionHeader} ${styles.trackSectionHeaderTight}`}
        >
          <div
            className={`${styles.trackSectionBar} ${styles.trackSectionBarPurple}`}
          />
          <div>
            <div className={styles.trackSectionTitle}>Annual Plans</div>
            <div className={styles.trackSectionSub}>
              Pay once upfront · monthly credit allocation · 12-month rollover
            </div>
          </div>
        </div>

        {/* Benefits bar */}
        {/* <div className={styles.benefitsBar}>
          {BENEFITS.map((b, i) => (
            <div key={i} className={styles.benefitItem}>
              <div className={styles.benefitIcon}>{b.icon}</div>
              <div className={styles.benefitLabel}>{b.label}</div>
              <div className={styles.benefitDesc}>
                {i === 2 ? `From ${annLow}/credit vs packs` : b.desc}
              </div>
            </div>
          ))}
        </div> */}

        {/* Annual plan cards */}
        <div className={styles.annualGrid}>
          {sortedAnnual?.map((plan) => {
            const isEnterprise = plan?.isCustomPricing;
            // Use priceNGN / priceUSD directly — no conversion math needed
            const displayPrice = isNGN ? plan?.priceNGN : plan?.priceUSD;
            const displayPerC =
              displayPrice && plan?.creditUnits
                ? displayPrice / plan?.creditUnits
                : null;
            const isPopular = plan.name === "Growth";
            const isActive = activePlanName === plan?.name;
            const accentClass = styles[PLAN_ACCENT[plan?.name]] ?? "";
            const features = buildAnnualFeatures(plan);

            return (
              <div
                key={plan._id}
                className={`${styles.planCard} ${accentClass} ${isPopular ? styles.planCardPopular : ""}`}
              >
                {isPopular && (
                  <div className={styles.planBadge}>Most Popular</div>
                )}
                <div
                  className={
                    isPopular
                      ? `${styles.planCardBody} ${styles.planCardBodyOffset}`
                      : styles.planCardBody
                  }
                >
                  <div className={styles.planName}>{plan.name}</div>

                  {!isEnterprise ? (
                    <div className={styles.planPriceBlock}>
                      <div className={styles.planPriceRow}>
                        <span className={styles.planPriceValue}>
                          {fmtCurrency(displayPrice, currency)}
                        </span>
                        <span className={styles.planPricePeriod}>/yr</span>
                      </div>
                      <div className={styles.planPriceSub}>
                        one-time payment · valid 1 year
                      </div>
                      {displayPerC && (
                        <div>
                          <span className={styles.planPerCreditChip}>
                            {fmtCurrency(displayPerC, currency)}/credit
                            effective
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.planPriceBlock}>
                      <div className={styles.planCustomPrice}>Custom</div>
                      <div className={styles.planCustomSub}>
                        negotiated pricing
                      </div>
                    </div>
                  )}

                  {isActive ? (
                    <button
                      className={`${styles.planCta} ${styles.planCtaCurrent}`}
                      disabled
                    >
                      ✓ Current Plan
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        isEnterprise ? onContactSales() : onStartPlan(plan)
                      }
                      className={`${styles.planCta} ${isPopular ? styles.planCtaPopular : ""}`}
                    >
                      {isEnterprise
                        ? "Contact Sales"
                        : `Pay ${fmtCurrency(displayPrice, currency)} Once`}
                    </button>
                  )}

                  {plan.creditUnits && (
                    <div className={styles.planAllocationBox}>
                      <div className={styles.planAllocationValue}>
                        {plan.creditUnits}C upfront
                      </div>
                      <div className={styles.planAllocationSub}>
                        valid 1 yr from purchase
                      </div>
                    </div>
                  )}

                  <div className={styles.planFeaturesBlock}>
                    <div className={styles.planFeaturesList}>
                      {features?.map((f, i) => (
                        <div key={i} className={styles.planFeatureRow}>
                          <span className={styles.planFeatureIcon}>✓</span>
                          <span className={styles.planFeatureText}>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.planTermsNote}>
          Annual plans renew once a year. Credits from the current year roll
          over into your next renewal.
        </div>
      </div>

      {/* ── Policy strip ── */}
      <div className={styles.policyStrip}>
        {POLICIES.map((p, i) => (
          <div key={p.title} className={styles.policyItem}>
            <div className={styles.policyIcon}>{p.icon}</div>
            <div className={styles.policyTitle}>{p.title}</div>
            <div className={styles.policyDesc}>
              {i === 3
                ? `Top up at ${topUpRange}/credit on any plan. Extends validity by 1 month only if less than a month remains.`
                : p.desc}
            </div>
          </div>
        ))}
      </div>

      {/* ── FAQ ── */}
      <FAQSection isNGN={isNGN} />
    </div>
  );
}
